import { io, Socket } from 'socket.io-client';

// ===================================
// Real-Time Event Types
// ===================================

export interface CampaignUpdate {
  campaign_id: string;
  campaign_name: string;
  status: 'running' | 'paused' | 'completed' | 'failed';
  metrics: {
    impressions: number;
    clicks: number;
    conversions: number;
    spend: number;
    revenue: number;
    ctr: number;
    roas: number;
  };
  timestamp: string;
}

export interface ConversionEvent {
  conversion_id: string;
  customer_id: string;
  campaign_id: string;
  campaign_name: string;
  conversion_type: 'purchase' | 'lead' | 'signup' | 'download';
  value: number;
  channel: string;
  timestamp: string;
}

export interface AgentActivity {
  agent_id: string;
  agent_type: string;
  agent_name: string;
  activity_type: 'task_started' | 'task_completed' | 'task_failed' | 'status_change';
  task_id?: string;
  task_type?: string;
  status: 'idle' | 'busy' | 'error';
  message: string;
  timestamp: string;
}

export interface AlertEvent {
  alert_id: string;
  alert_type: 'anomaly' | 'budget' | 'performance' | 'conflict' | 'system';
  severity: 'info' | 'warning' | 'error' | 'critical';
  title: string;
  message: string;
  entity_id?: string;
  entity_type?: 'campaign' | 'agent' | 'channel';
  timestamp: string;
  requires_action: boolean;
}

export interface DashboardSnapshot {
  snapshot_id: string;
  total_campaigns: number;
  active_campaigns: number;
  total_agents: number;
  active_agents: number;
  total_conversions_today: number;
  total_revenue_today: number;
  total_spend_today: number;
  average_roas: number;
  top_performing_campaign?: {
    campaign_id: string;
    campaign_name: string;
    roas: number;
  };
  timestamp: string;
}

export interface ChannelPerformance {
  channel: string;
  impressions: number;
  clicks: number;
  conversions: number;
  spend: number;
  revenue: number;
  ctr: number;
  cpc: number;
  roas: number;
  timestamp: string;
}

export interface BudgetAlert {
  campaign_id: string;
  campaign_name: string;
  budget_total: number;
  budget_used: number;
  budget_remaining: number;
  percent_used: number;
  pace: 'under' | 'on_track' | 'over';
  timestamp: string;
}

// ===================================
// Event Callback Types
// ===================================

export type EventCallback<T> = (data: T) => void;

export interface RealtimeEventCallbacks {
  'campaign:update'?: EventCallback<CampaignUpdate>;
  'conversion:new'?: EventCallback<ConversionEvent>;
  'agent:activity'?: EventCallback<AgentActivity>;
  'alert:new'?: EventCallback<AlertEvent>;
  'dashboard:snapshot'?: EventCallback<DashboardSnapshot>;
  'channel:performance'?: EventCallback<ChannelPerformance>;
  'budget:alert'?: EventCallback<BudgetAlert>;
}

// ===================================
// WebSocket Service
// ===================================

class WebSocketService {
  private socket: Socket | null = null;
  private eventCallbacks: Map<string, Set<Function>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private connectionFailed = false;

  constructor() {
    this.connect();
  }

  /**
   * Connect to WebSocket server
   */
  connect(url?: string) {
    if (this.socket?.connected) {
      return;
    }

    // Skip if already permanently failed
    if (this.connectionFailed) {
      return;
    }

    // Get WebSocket URL from environment variable or derive from API URL
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';
    const baseUrl = apiUrl.replace(/\/api\/v1$/, ''); // Remove /api/v1 suffix

    // Determine WebSocket URL
    let wsUrl = url || import.meta.env.VITE_WS_URL;

    // If no explicit WS URL provided, determine based on environment
    if (!wsUrl) {
      if (import.meta.env.PROD) {
        // In production, always use Railway backend directly for WebSocket
        // This avoids proxy issues with domains like api.synapmarketing.com
        wsUrl = 'https://marketing-ai-platform-production.up.railway.app';
      } else {
        // In development, derive from API URL
        wsUrl = baseUrl;
      }
    }

    // Only log in development
    if (import.meta.env.DEV) {
      console.log('🔌 Connecting to WebSocket server:', wsUrl);
    }

    this.socket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: this.maxReconnectAttempts,
      timeout: 10000,
    });

    // Connection handlers
    this.socket.on('connect', () => {
      if (import.meta.env.DEV) {
        console.log('✅ WebSocket connected:', this.socket?.id);
      }
      this.reconnectAttempts = 0;
      this.connectionFailed = false;
    });

    this.socket.on('disconnect', (reason) => {
      if (import.meta.env.DEV) {
        console.log('❌ WebSocket disconnected:', reason);
      }
    });

    this.socket.on('connect_error', (error) => {
      this.reconnectAttempts++;

      // Only log detailed errors in development
      if (import.meta.env.DEV) {
        console.warn('🔴 WebSocket connection error:', error.message);
      }

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        // Mark as permanently failed to stop retry attempts
        this.connectionFailed = true;
        if (import.meta.env.DEV) {
          console.warn('WebSocket: Max reconnection attempts reached, disabling real-time updates');
        }
        // Disconnect to stop further attempts
        this.socket?.disconnect();
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      if (import.meta.env.DEV) {
        console.log(`🔄 Reconnected after ${attemptNumber} attempts`);
      }
    });

    // Register all event listeners
    this.registerEventListeners();
  }

  /**
   * Register all event listeners
   */
  private registerEventListeners() {
    if (!this.socket) return;

    const events: Array<keyof RealtimeEventCallbacks> = [
      'campaign:update',
      'conversion:new',
      'agent:activity',
      'alert:new',
      'dashboard:snapshot',
      'channel:performance',
      'budget:alert',
    ];

    events.forEach((event) => {
      this.socket!.on(event, (data: any) => {
        this.emit(event, data);
      });
    });
  }

  /**
   * Subscribe to dashboard updates
   */
  subscribeToDashboard() {
    if (!this.socket) {
      console.error('Socket not connected');
      return;
    }
    this.socket.emit('subscribe:dashboard');
  }

  /**
   * Subscribe to campaign updates
   */
  subscribeToCampaigns() {
    if (!this.socket) {
      console.error('Socket not connected');
      return;
    }
    this.socket.emit('subscribe:campaigns');
  }

  /**
   * Subscribe to agent updates
   */
  subscribeToAgents() {
    if (!this.socket) {
      console.error('Socket not connected');
      return;
    }
    this.socket.emit('subscribe:agents');
  }

  /**
   * Subscribe to conversion events
   */
  subscribeToConversions() {
    if (!this.socket) {
      console.error('Socket not connected');
      return;
    }
    this.socket.emit('subscribe:conversions');
  }

  /**
   * Subscribe to alerts
   */
  subscribeToAlerts() {
    if (!this.socket) {
      console.error('Socket not connected');
      return;
    }
    this.socket.emit('subscribe:alerts');
  }

  /**
   * Subscribe to all channels
   */
  subscribeToAll() {
    this.subscribeToDashboard();
    this.subscribeToCampaigns();
    this.subscribeToAgents();
    this.subscribeToConversions();
    this.subscribeToAlerts();
  }

  /**
   * Add event listener
   */
  on<K extends keyof RealtimeEventCallbacks>(
    event: K,
    callback: RealtimeEventCallbacks[K]
  ) {
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, new Set());
    }
    this.eventCallbacks.get(event)!.add(callback as Function);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Remove event listener
   */
  off<K extends keyof RealtimeEventCallbacks>(
    event: K,
    callback?: RealtimeEventCallbacks[K]
  ) {
    if (!callback) {
      this.eventCallbacks.delete(event);
      return;
    }

    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      callbacks.delete(callback as Function);
    }
  }

  /**
   * Emit event to all listeners
   */
  private emit(event: string, data: any) {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => callback(data));
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Get connection status
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get socket ID
   */
  getSocketId(): string | undefined {
    return this.socket?.id;
  }
}

// Singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
